import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Button,
  Col,
  Collapse,
  Form,
  Input,
  message,
  notification,
  Row,
  Select,
  Switch,
  Tooltip,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  ScanOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import { getUserTypes } from '../../../../services/taxonomies/user-types';
import { getPosDocTypes } from '../../../../services/taxonomies/doc-types';
import useDictionaries from '../../../../hooks/useDictionaries';
import { getInstitutions } from '../../../../services/taxonomies/institutions';
import { getSubdivisions } from '../../../../services/taxonomies/subdivisions';
import { getUserFilesById } from '../../../../services/admin/users';
import {
  downloadFiles,
  getFiles,
  uploadScan,
} from '../../../../services/files';
import { changeAttachmentsToOrder } from '../../../../services/pos/pos';
import POSContext from '../../POSContext';
import DatePickerLocale from '../../../DatePickerLocale';
import UserTypesFields from './UserTypesFields';
import FileUploader from '../../../FileUploader';
import { scan } from '../../../../helpers/scanner';
import { getCountries } from '../../../../services/taxonomies/countires';
import {
  PriorityCountries,
  EnvToPriorityDistrictMap,
} from '../../../../helpers/constants';
import { MIFARE_TYPES } from '../../../../pages/orders/constants';

const dictionaries = {
  userTypes: getUserTypes,
  docTypes: getPosDocTypes,
  countries: () =>
    getCountries({ pageSize: 200, criterias: { deleted: 'false' } }),
};

// cicd

const populateInstitutions = (userTypeId, userTypes, setInstitutions) => {
  const insts =
    userTypes.find((ut) => ut.id === userTypeId)?.institutions || [];

  if (insts.length) {
    getInstitutions({ pageSize: 100, criterias: { ids: insts } }).then(
      (res) => {
        setInstitutions(res.content);
      },
    );
  } else {
    setInstitutions([]);
  }
};

const populateSubdivisions = (institutionId, institutions, setSubdivisions) => {
  const subdivisions =
    institutions.find((i) => i.id === institutionId)?.subdivisions || [];
  if (subdivisions.length) {
    getSubdivisions({ pageSize: 100, criterias: { ids: subdivisions } }).then(
      (res) => {
        setSubdivisions(res.content);
      },
    );
  } else {
    setSubdivisions([]);
  }
};

const AccountDetailsForm = () => {
  const { t } = useTranslation();
  const { shift, order, setOrder, setAttachmentForm } = useContext(POSContext);

  const { account, card = {}, products } = order || {};
  const orderProducts = Array.isArray(products) ? products : [];

  const {
    userId,
    userProfileTypeId,
    institutionId,
    subdivisionId,
    userPhone,
    address2,
    metaData1,
    metaData2,
    attachments,
  } = account || {};
  const [form] = Form.useForm();
  const disabledDate = (current) => {
    // Can not select days before today and today
    return current < moment().add(-1, 'day');
  };

  const [institutions, setInstitutions] = useState([]);
  const [subdivisions, setSubdivisions] = useState([]);
  const [userFiles, setUserFiles] = useState([]);

  const [{ docTypes, userTypes, countries }] = useDictionaries(dictionaries);
  const phoneRegex = useMemo(() => /^(?:\+?\d{10,14}|[-])$/, []);
  const countryId = Form.useWatch(['address2', 'countryId'], form);

  const isNominal = !orderProducts.some(
    (p) => p?.code === MIFARE_TYPES.MIFARE_1K_NENOMINAL,
  );

  const environment = useMemo(() => window._env_.ENVIROMENT, []);
  const priorityDistrictName = useMemo(
    () => EnvToPriorityDistrictMap[environment] || null,
    [environment],
  );

  const countryOptions = useMemo(() => {
    const rawCountries = countries?.content || [];

    const sortedCountries = [...rawCountries].sort((a, b) => {
      if (a.name === PriorityCountries.ROMANIA) return -1;
      if (b.name === PriorityCountries.ROMANIA) return 1;

      return a.name.localeCompare(b.name, 'ro');
    });

    return sortedCountries.map((country) => ({
      label: country.name,
      value: country.id,
      disabled: country.deleted,
    }));
  }, [countries?.content]);

  const districtOptions = useMemo(() => {
    const districts =
      (countries?.content || []).find((country) => country.id === countryId)
        ?.districts || [];

    return [...districts]
      .sort((a, b) => {
        if (a.name === priorityDistrictName) return -1;
        if (b.name === priorityDistrictName) return 1;

        return a.name.localeCompare(b.name, 'ro');
      })
      .map((district) => ({
        label: district.name,
        value: district.id,
        disabled: district.deleted,
      }));
  }, [countries?.content, countryId, priorityDistrictName]);

  const scanEnableValue = window._env_.SCAN_ENABLE;
  const scanEnable = scanEnableValue === 'true';

  useEffect(() => {
    getUserFilesById(userId)
      .then((res) => {
        if (res.length > 0) {
          getFiles(res.map((item) => item.id)).then((res2) => {
            // merge 2 arrays by fileId
            const mergedMap = [...res, ...res2].reduce((acc, obj) => {
              acc[obj.id] = { ...acc[obj.id], ...obj }; // Merge objects with the same ID
              return acc;
            }, {});
            const mergedArray = Object.values(mergedMap);

            // group files by docTypeId and the the latest updatedDate
            const groupedDocs = Object.values(
              mergedArray.reduce((acc, doc) => {
                // If docTypeId doesn't exist or the new document has an earlier uploadDate, replace it
                if (
                  !acc[doc.docTypeId] ||
                  new Date(doc.uploadDate) >
                    new Date(acc[doc.docTypeId].uploadDate)
                ) {
                  acc[doc.docTypeId] = doc;
                }
                return acc;
              }, {}),
            );

            const usrFiles = groupedDocs.map((item) => {
              const dt = docTypes.content.find(
                (it) => it.id === item.docTypeId,
              );
              return {
                id: item.id,
                docName: dt?.name || '',
                fileName: item.name,
                expirationDate: item?.expirationDate,
                uploadDate: item?.uploadDate,
                fileSize: item?.size,
                docTypeId: item?.docTypeId,
              };
            });
            setUserFiles(usrFiles);
          });
        }
      })
      .catch((err) => console.error(err));
  }, [userId, docTypes.content]);

  useEffect(() => {
    const attch = (attachments || []).map((item) => {
      const docType = docTypes.content.find((dt) => dt.id === item.documentId);
      return {
        ...item,
        docTypeName:
          docTypes.content.find((item1) => item.documentId === item1.id)
            ?.name || null,
        expirationDate: item.expirationDate
          ? moment(item.expirationDate)
          : null,
        verified: item.verified || false,
        hasExpirationDate: docType?.hasExpirationDate,
        dateChangeable: docType?.dateChangeable,
        durationType: docType?.durationType,
        documentName: item?.documentName,
      };
    });
    const userTypeDocIds =
      userTypes.content.find((c) => c.id === userProfileTypeId)?.docTypes || [];
    const usrTypeDocs = docTypes.content
      .filter((dt) => userTypeDocIds.includes(dt.id))
      .filter((dt) => !dt.deleted);

    const accountAddress = address2 || {};

    const formVal = {
      userTypeId: userProfileTypeId,
      institutionId,
      subdivisionId,
      ...(card.isNominal ? { userPhone } : {}),
      address2: {
        countryId: accountAddress.countryId ?? null,
        districtId: accountAddress.districtId ?? null,
        locality: accountAddress.locality ?? null,
        street: accountAddress.street ?? null,
        number: accountAddress.number ?? null,
        building: accountAddress.building ?? null,
        apartment: accountAddress.apartment ?? null,
      },
      metaData1,
      metaData2,
      files: attachments?.length
        ? attch
        : usrTypeDocs.map((r) => ({
            hasExpirationDate: r.hasExpirationDate,
            dateChangeable: r.dateChangeable,
            durationType: r.durationType,
            documentId: r.id,
            docTypeName: r.name,
            expirationDate: r.expirationDate ? moment(r.expirationDate) : null,
            verified: false,
            documentName: null,
          })),
    };
    form.setFieldsValue(formVal);
    setAttachmentForm(formVal);
  }, [
    attachments,
    userTypes,
    form,
    userProfileTypeId,
    docTypes.content,
    institutionId,
    address2,
    countries?.content,
    priorityDistrictName,
    metaData1,
    metaData2,
    subdivisionId,
    userPhone,
    card.isNominal,
    setAttachmentForm,
  ]);

  useEffect(() => {
    if (userProfileTypeId) {
      populateInstitutions(
        userProfileTypeId,
        userTypes.content,
        setInstitutions,
      );
    }
  }, [userTypes.content, userProfileTypeId]);

  useEffect(() => {
    if (institutionId) {
      populateSubdivisions(institutionId, institutions, setSubdivisions);
    }
  }, [institutionId, institutions, setSubdivisions]);

  const saveAttachments = useCallback(
    (overrideValues = {}) => {
      const value = {
        ...form.getFieldsValue(),
        ...overrideValues,
      };

      setAttachmentForm(value);

      return changeAttachmentsToOrder(shift, order, {
        ...value,
        files: value.files,
      })
        .then((res) => {
          setOrder(res);
        })
        .catch((err) => {
          console.error(err);
          message.error({
            content: err.inner?._
              ? t(err.inner._)
              : t('entity.pos.errors.productNotAdded'),
            key: 'pos',
            duration: 5,
            className: 'card-message',
          });
        });
    },
    [form, order, setAttachmentForm, setOrder, shift, t],
  );

  const changeAttachments = useCallback(() => {
    saveAttachments();
  }, [saveAttachments]);

  const saveAddress = useCallback(() => {
    const currentAddress2 = form.getFieldValue('address2') || {};

    return saveAttachments({
      address2: currentAddress2,
    });
  }, [form, saveAttachments]);

  const updateAddressField = useCallback(
    (fieldName, value, shouldSave = false, extraFields = {}) => {
      const currentAddress2 = form.getFieldValue('address2') || {};

      const nextAddress2 = {
        ...currentAddress2,
        [fieldName]: value || null,
        ...extraFields,
      };

      form.setFieldsValue({
        address2: nextAddress2,
      });

      setAttachmentForm((prev) => ({
        ...prev,
        address2: nextAddress2,
      }));

      if (shouldSave) {
        return saveAttachments({
          address2: nextAddress2,
        });
      }

      return Promise.resolve();
    },
    [form, saveAttachments, setAttachmentForm],
  );

  const handleChangeUserType = useCallback(
    (val) => {
      const currentAddress2 = form.getFieldValue('address2');

      form.resetFields();

      const userTypeDocIds =
        userTypes.content.find((c) => c.id === val)?.docTypes || [];
      const usrTypeDocs = docTypes.content
        .filter((dt) => userTypeDocIds.includes(dt.id))
        .filter((dt) => !dt.deleted);
      const formVal = {
        userTypeId: val,
        userPhone,
        address2: currentAddress2,
        files: usrTypeDocs.map((r) => ({
          documentId: r.id,
          docTypeName: r.name,
          expirationDate: r.expirationDate ? moment(r.expirationDate) : null,
          hasExpirationDate: r.hasExpirationDate,
          dateChangeable: r.dateChangeable,
          durationType: r.durationType,
          verified: false,
          documentName: null,
        })),
      };
      form.setFieldsValue(formVal);
      setAttachmentForm(formVal);
      populateInstitutions(val, userTypes.content, setInstitutions);
      setSubdivisions([]);
      changeAttachments();
    },
    [
      form,
      userTypes.content,
      docTypes.content,
      setAttachmentForm,
      changeAttachments,
      userPhone,
    ],
  );

  const handleChangeInstitution = useCallback(
    (val) => {
      setSubdivisions([]);

      const {
        userTypeId,
        files,
        address2: currentAddress2,
      } = form.getFieldsValue();

      form.resetFields();

      form.setFieldsValue({
        userTypeId,
        institutionId: val,
        files,
        userPhone,
        address2: currentAddress2,
      });
      populateSubdivisions(val, institutions, setSubdivisions);
      changeAttachments();
    },
    [form, institutions, changeAttachments, userPhone],
  );

  const handleChangeSubdiv = useCallback(() => {
    changeAttachments();
  }, [changeAttachments]);

  const markFileAsVerified = useCallback(
    (field) => {
      if (scanEnable) {
        const doc = form.getFieldsValue(['files'])?.files[field.name];
        let updatedDoc = null;
        const uf = userFiles.find((it) => it.docTypeId === doc.documentId);

        if (doc.verified) {
          updatedDoc = {
            ...doc,
            fileId: uf?.id,
            documentName: uf?.fileName,
          };
        } else {
          updatedDoc = {
            ...doc,
            documentName: '',
          };
        }
        form.setFieldsValue({
          files: {
            ...form.getFieldValue('files'),
            [field.name]: updatedDoc,
          },
        });
      }

      changeAttachments();
    },
    [form, changeAttachments, userFiles, scanEnable],
  );

  const isBiggerThan5MB = (bytes) => {
    const MB = 1024 * 1024;
    return bytes > 5 * MB;
  };

  const scanFile = useCallback(
    (field) => {
      scan()
        .then(
          (resp) => (resp.ok ? resp.blob() : Promise.resolve(0)),
          () => Promise.resolve(0),
        )
        .then((scanData) => {
          if (!scanData) {
            notification.error({
              message: t('errors.uploadError'),
            });
            throw new Error('Scan failed or returned no data');
          }

          if (isBiggerThan5MB(scanData.size)) {
            notification.error({
              message: t('errors.maxScannedFileSize', { maxSize: 5 }),
            });
            throw new Error('File is too big');
          }
          const documentType = form.getFieldsValue(['files'])?.files[
            field.name
          ];
          return uploadScan([scanData], documentType?.docTypeName);
        })
        .then((fileResponse) => {
          return fileResponse.json();
        })
        .then((res) => {
          const doc = form.getFieldsValue(['files'])?.files[field.name];
          if (res && doc) {
            const updatedDoc = {
              ...doc,
              fileId: res[0]?.id,
              documentName: res[0]?.name,
              verified: true,
            };
            form.setFieldsValue({
              files: {
                ...form.getFieldValue('files'),
                [field.name]: updatedDoc,
              },
            });

            changeAttachments();
          }
          notification.success({
            message: t('errors.uploadSuccessful'),
          });
        })
        .catch((error) => {
          console.error('Error in scan or upload:', error);
        });
    },
    [form, t, changeAttachments],
  );

  const addFiles = useCallback(
    (files, field) => {
      const file = files[0];
      const doc = form.getFieldsValue(['files'])?.files[field.name];
      if (file && doc) {
        const updatedDoc = {
          ...doc,
          fileId: file?.id,
          documentName: file?.name,
          verified: true,
        };
        form.setFieldsValue({
          files: {
            ...form.getFieldValue('files'),
            [field.name]: updatedDoc,
          },
        });
        changeAttachments();
      }
    },
    [form, changeAttachments],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleChangePhoneNumber = useCallback(
    debounce((event) => {
      const phone = event.target.value;
      form.setFieldsValue({ userPhone: phone });
      setAttachmentForm((prev) => ({ ...prev, userPhone: phone }));

      if (phoneRegex.test(phone)) {
        changeAttachments();
      }
    }, 500),
    [changeAttachments],
  );

  const viewUserFile = useCallback(
    (fileId) => {
      if (fileId) {
        downloadFiles(fileId);
      } else {
        notification.error({
          message: t('errors.downloadError'),
        });
      }
    },
    [t],
  );

  const viewFile = useCallback(
    (field) => {
      const file = form.getFieldsValue(['files'])?.files[field.name];
      const fileId = file?.fileId;
      if (fileId) {
        downloadFiles(fileId);
      } else {
        notification.error({
          message: t('errors.downloadError'),
        });
      }
    },
    [form, t],
  );

  return (
    <Form form={form} layout="vertical">
      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32, xl: 40 }}>
        <Col span={24}>
          <Form.Item
            name="userTypeId"
            label={t('entity.pos.existingCard.userType')}
          >
            <Select onChange={handleChangeUserType} disabled={!card.isNominal}>
              {userTypes.content.map((ut) => (
                <Select.Option
                  key={ut.id}
                  value={ut.id}
                  disabled={ut.disabled || ut.deleted}
                >
                  {ut.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32, xl: 40 }}>
        <Col span={24}>
          <Form.Item
            name="institutionId"
            label={t('entity.pos.existingCard.institution')}
          >
            <Select
              onChange={handleChangeInstitution}
              allowClear
              disabled={!card.isNominal}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {institutions.map((i) => (
                <Select.Option
                  key={i.id}
                  value={i.id}
                  disabled={i.disabled || i.deleted}
                >
                  {i.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row
        gutter={{ xs: 8, sm: 16, md: 24, lg: 32, xl: 40 }}
        hidden={!card.isNominal}
      >
        <Col span={24}>
          <Form.Item
            name="userPhone"
            label={t('entity.pos.existingCard.phoneNumber')}
            rules={[
              {
                validator: (_, value) => {
                  if (!value) {
                    return Promise.resolve();
                  }

                  if (value && phoneRegex.test(value)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(t('entity.pos.errors.invalidPhoneNumber')),
                  );
                },
              },
            ]}
          >
            <Input
              onChange={handleChangePhoneNumber}
              hidden={!card.isNominal}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32, xl: 40 }}>
        <Col span={24}>
          <Collapse defaultActiveKey={['addressPanel']} ghost>
            <Collapse.Panel
              header={t('entity.pos.cardReplacement.address')}
              key="addressPanel"
              forceRender
              style={{ padding: 0 }}
            >
              <Row gutter={8}>
                <Col span={8}>
                  <Form.Item
                    name={['address2', 'countryId']}
                    label={t('entity.admin.user.address.country')}
                    rules={[
                      {
                        required: isNominal,
                        message: t('errors.notEmpty'),
                      },
                    ]}
                  >
                    <Select
                      options={countryOptions}
                      loading={countries?.loading}
                      allowClear
                      showSearch
                      filterOption={(input, option) =>
                        option.label.toLowerCase().includes(input.toLowerCase())
                      }
                      onChange={(selectedCountryId) =>
                        updateAddressField(
                          'countryId',
                          selectedCountryId,
                          true,
                          {
                            districtId: null,
                          },
                        )
                      }
                    />
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item
                    name={['address2', 'districtId']}
                    label={t('entity.admin.user.address.district')}
                    rules={[
                      {
                        required: isNominal,
                        message: t('errors.notEmpty'),
                      },
                    ]}
                  >
                    <Select
                      options={districtOptions}
                      allowClear
                      showSearch
                      filterOption={(input, option) =>
                        option.label.toLowerCase().includes(input.toLowerCase())
                      }
                      disabled={!countryId}
                      onChange={(selectedDistrictId) =>
                        updateAddressField(
                          'districtId',
                          selectedDistrictId,
                          true,
                        )
                      }
                    />
                  </Form.Item>
                </Col>

                <Col span={10}>
                  <Form.Item
                    name={['address2', 'locality']}
                    label={t('entity.admin.user.address.locality')}
                    rules={[
                      {
                        required: isNominal,
                        whitespace: true,
                        message: t('errors.notEmpty'),
                      },
                    ]}
                  >
                    <Input
                      maxLength={32}
                      allowClear
                      onChange={(event) =>
                        updateAddressField('locality', event.target.value)
                      }
                      onBlur={saveAddress}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item
                    name={['address2', 'street']}
                    label={t('entity.admin.user.address.street')}
                  >
                    <Input
                      maxLength={32}
                      allowClear
                      onChange={(event) =>
                        updateAddressField('street', event.target.value)
                      }
                      onBlur={saveAddress}
                    />
                  </Form.Item>
                </Col>

                <Col span={4}>
                  <Form.Item
                    name={['address2', 'number']}
                    label={t('entity.admin.user.address.number')}
                  >
                    <Input
                      maxLength={32}
                      allowClear
                      onChange={(event) =>
                        updateAddressField('number', event.target.value)
                      }
                      onBlur={saveAddress}
                    />
                  </Form.Item>
                </Col>

                <Col span={4}>
                  <Form.Item
                    name={['address2', 'building']}
                    label={t('entity.admin.user.address.building')}
                  >
                    <Input
                      maxLength={8}
                      allowClear
                      onChange={(event) =>
                        updateAddressField('building', event.target.value)
                      }
                      onBlur={saveAddress}
                    />
                  </Form.Item>
                </Col>

                <Col span={4}>
                  <Form.Item
                    name={['address2', 'apartment']}
                    label={t('entity.admin.user.address.ap')}
                  >
                    <Input
                      maxLength={8}
                      allowClear
                      onChange={(event) =>
                        updateAddressField('apartment', event.target.value)
                      }
                      onBlur={saveAddress}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Collapse.Panel>
          </Collapse>
        </Col>
      </Row>

      <UserTypesFields
        subdivisions={subdivisions}
        showColumns={
          userTypes?.content.find(
            (ut) => ut.id === form.getFieldValue(['userTypeId']),
          )?.showColumns
        }
        handleChangeSubdiv={handleChangeSubdiv}
        changeAttachments={changeAttachments}
      />

      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32, xl: 40 }}>
        <Col span={24}>
          <Row>
            <h3>{t('entity.accountsDetails.oldUserFiles')}</h3>
          </Row>
          {userFiles.map((item) => (
            <Row
              key={item.id}
              gutter={{ xs: 8, sm: 16, md: 24, lg: 32, xl: 40 }}
              style={{ padding: '0.5rem 0', alignItems: 'flex-start' }}
            >
              <Col span={scanEnable ? 5 : 7}>
                <Row
                  style={{
                    height: '100%',
                    maxWidth: '100%',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    lineHeight: '22px',
                  }}
                >
                  {item.docName}
                </Row>
              </Col>
              <Col span={scanEnable ? 7 : 10}>
                <Row>{t('entity.pos.existingCard.expirationDate')}</Row>
                <Row>
                  <DatePickerLocale
                    disabled
                    format="DD.MM.YYYY"
                    value={
                      item.expirationDate
                        ? moment(item.expirationDate)
                        : item.expirationDate
                    }
                    style={{ width: '100%' }}
                  />
                </Row>
              </Col>
              {scanEnable && (
                <>
                  <Col span={8}>
                    <Row>{t('entity.pos.existingCard.uploadDate')}</Row>
                    <Row>
                      <DatePickerLocale
                        disabled
                        format="DD.MM.YYYY"
                        value={
                          item.uploadDate
                            ? moment(item.uploadDate)
                            : item.uploadDate
                        }
                        style={{ width: '100%' }}
                      />
                    </Row>
                  </Col>
                  <Col span={4}>
                    <Tooltip
                      title={item.fileName}
                      placement="top"
                      arrowPointAtCenter
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          width: '120px', // Adjust this width as needed
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          verticalAlign: 'middle', // Align text vertically in the middle if needed
                        }}
                      >
                        {item.fileName}
                      </span>
                    </Tooltip>

                    {item.fileSize > 0 && (
                      <Button onClick={() => viewUserFile(item.id)}>
                        <EyeOutlined />
                      </Button>
                    )}
                  </Col>
                </>
              )}
            </Row>
          ))}
          <Row>
            <h3>{t('entity.accountsDetails.newUserFiles')}</h3>
          </Row>
          <Form.List name="files">
            {(fields) => {
              return (
                <>
                  {fields.map((field) => {
                    const hasExpirationDate = form.getFieldValue([
                      'files',
                      field.name,
                      'hasExpirationDate',
                    ]);

                    return (
                      <Row
                        gutter={{ xs: 8, sm: 16, md: 24, lg: 32, xl: 40 }}
                        key={field.key}
                        style={{
                          padding: '0.5rem 0',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Col span={scanEnable ? 6 : 7}>
                          <Form.Item
                            noStyle
                            name={[field.name, 'fileId']}
                            fieldKey={[field.fieldKey, 'fileId']}
                          >
                            <Input type="hidden" />
                          </Form.Item>
                          <Form.Item noStyle shouldUpdate>
                            {() => (
                              <div
                                style={{
                                  maxWidth: '100%',
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                  overflowWrap: 'break-word',
                                  lineHeight: '22px',
                                }}
                              >
                                {form.getFieldValue([
                                  'files',
                                  field.name,
                                  'docTypeName',
                                ])}
                              </div>
                            )}
                          </Form.Item>
                        </Col>

                        {!hasExpirationDate && (
                          <Col span={scanEnable ? 7 : 10} />
                        )}

                        {hasExpirationDate && (
                          <Col span={scanEnable ? 7 : 10}>
                            <Form.Item
                              {...field}
                              name={[field.name, 'expirationDate']}
                              fieldKey={[field.fieldKey, 'expirationDate']}
                              key={field.fieldKey}
                              label={t(
                                'entity.pos.existingCard.expirationDate',
                              )}
                              style={{ marginBottom: 0 }}
                            >
                              <DatePickerLocale
                                disabled={
                                  form.getFieldValue([
                                    'files',
                                    field.name,
                                    'durationType',
                                  ]) &&
                                  !form.getFieldValue([
                                    'files',
                                    field.name,
                                    'dateChangeable',
                                  ])
                                }
                                format="DD.MM.YYYY"
                                style={{ width: '100%' }}
                                onChange={changeAttachments}
                                disabledDate={disabledDate}
                              />
                            </Form.Item>
                          </Col>
                        )}
                        <Col
                          span={scanEnable ? 4 : 7}
                          style={{ paddingLeft: '12px' }}
                        >
                          <Form.Item
                            {...field}
                            name={[field.name, 'verified']}
                            fieldKey={[field.fieldKey, 'verified']}
                            key={field.fieldKey}
                            label={t('entity.pos.existingCard.isVerified')}
                            style={{ marginBottom: 0 }}
                          >
                            <Switch
                              checked={
                                form.getFieldsValue(['files'])?.files[
                                  field.name
                                ]?.verified
                              }
                              checkedChildren={<CheckOutlined />}
                              unCheckedChildren={<CloseOutlined />}
                              onChange={() => markFileAsVerified(field)}
                            />
                          </Form.Item>
                        </Col>

                        {scanEnable && (
                          <>
                            <Col span={7}>
                              <Row>
                                <Tooltip
                                  title={form.getFieldValue([
                                    'files',
                                    field.name,
                                    'documentName',
                                  ])}
                                  placement="top"
                                  arrowPointAtCenter
                                >
                                  <span
                                    style={{
                                      display: 'inline-block',
                                      width: '100px',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      verticalAlign: 'middle',
                                    }}
                                  >
                                    {form.getFieldValue([
                                      'files',
                                      field.name,
                                      'documentName',
                                    ])}
                                  </span>
                                </Tooltip>
                              </Row>

                              <Row>
                                <Button onClick={() => scanFile(field)}>
                                  <ScanOutlined />
                                </Button>

                                <FileUploader
                                  onUploadSuccess={(files) =>
                                    addFiles(files, field)
                                  }
                                  multiple={false}
                                  dragger={false}
                                />
                                <Button
                                  onClick={() => viewFile(field)}
                                  disabled={
                                    !form.getFieldValue([
                                      'files',
                                      field.name,
                                      'fileId',
                                    ])
                                  }
                                >
                                  <EyeOutlined />
                                </Button>
                              </Row>
                            </Col>
                          </>
                        )}
                      </Row>
                    );
                  })}
                </>
              );
            }}
          </Form.List>
        </Col>
      </Row>
    </Form>
  );
};

export default AccountDetailsForm;
